import { useState } from 'react'
import { toast } from 'sonner'
import { AlertTriangle, Power, RefreshCw, User } from 'lucide-react'

import PageContainer from '@/common/components/PageContainer'
import { Button } from '@/common/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Input } from '@/common/ui/input'
import { Label } from '@/common/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/common/ui/alert'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationKickAllSessions from '../../api/hooks/useMutationKickAllSessions'
import useMutationReinitAllSessions from '../../api/hooks/useMutationReinitAllSessions'
import useMutationKickPlayer from '../../api/hooks/useMutationKickPlayer'
import useMutationReinitPlayer from '../../api/hooks/useMutationReinitPlayer'
import { getPlayerByPhone } from '../../api/services'

import type { SessionActionType } from '../../type'

type PlayerActionType = 'kick' | 'reinit' | null

export default function SessionManagementPage() {
	const [activeModal, setActiveModal] = useState<SessionActionType | null>(null)
	const [confirmText, setConfirmText] = useState('')

	// Player-specific actions
	const [playerActionDialog, setPlayerActionDialog] =
		useState<PlayerActionType>(null)
	const [playerPhone, setPlayerPhone] = useState('')
	const [isSearchingPlayer, setIsSearchingPlayer] = useState(false)

	const { mutate: kickAll, isPending: isKicking } = useMutationKickAllSessions()
	const { mutate: reinitAll, isPending: isReiniting } =
		useMutationReinitAllSessions()

	const { mutate: kickPlayer, isPending: isKickingPlayer } =
		useMutationKickPlayer()
	const { mutate: reinitPlayer, isPending: isReinitingPlayer } =
		useMutationReinitPlayer()

	const isConfirmTextValid = confirmText === 'ยืนยัน'
	const isProcessing = isKicking || isReiniting
	const isPlayerProcessing =
		isKickingPlayer || isReinitingPlayer || isSearchingPlayer

	const handleOpenModal = (type: SessionActionType) => {
		setActiveModal(type)
		setConfirmText('')
	}

	const handleCloseModal = () => {
		if (isProcessing) return
		setActiveModal(null)
		setConfirmText('')
	}

	const handleOpenPlayerAction = (type: PlayerActionType) => {
		setPlayerActionDialog(type)
		setPlayerPhone('')
	}

	const handlePlayerKick = async () => {
		if (!playerPhone.trim()) return

		setIsSearchingPlayer(true)
		try {
			// Search for player by phone
			const playersData = await getPlayerByPhone(playerPhone)

			if (!playersData.data || playersData.data.length === 0) {
				toast.error(`ไม่พบผู้เล่นที่มีเบอร์โทร ${playerPhone}`)
				setIsSearchingPlayer(false)
				return
			}

			const player = playersData.data[0]

			// Kick player by ID
			kickPlayer(player.id, {
				onSuccess: () => {
					toast.success(
						`เตะผู้เล่น ${player.username || player.phone} ออกจากเกมเรียบร้อยแล้ว`,
					)
					setPlayerActionDialog(null)
					setPlayerPhone('')
					setIsSearchingPlayer(false)
				},
				onError: (error) => {
					showErrorToast(error)
					setIsSearchingPlayer(false)
				},
			})
		} catch (error) {
			showErrorToast(error as Error)
			setIsSearchingPlayer(false)
		}
	}

	const handlePlayerReinit = async () => {
		if (!playerPhone.trim()) return

		setIsSearchingPlayer(true)
		try {
			// Search for player by phone
			const playersData = await getPlayerByPhone(playerPhone)

			if (!playersData.data || playersData.data.length === 0) {
				toast.error(`ไม่พบผู้เล่นที่มีเบอร์โทร ${playerPhone}`)
				setIsSearchingPlayer(false)
				return
			}

			const player = playersData.data[0]

			// Reinit player by ID
			reinitPlayer(player.id, {
				onSuccess: () => {
					toast.success(
						`รีเซ็ตเซสชันของผู้เล่น ${player.username || player.phone} เรียบร้อยแล้ว`,
					)
					setPlayerActionDialog(null)
					setPlayerPhone('')
					setIsSearchingPlayer(false)
				},
				onError: (error) => {
					showErrorToast(error)
					setIsSearchingPlayer(false)
				},
			})
		} catch (error) {
			showErrorToast(error as Error)
			setIsSearchingPlayer(false)
		}
	}

	const handleConfirm = () => {
		if (!isConfirmTextValid || !activeModal) return

		const mutationFn = activeModal === 'kick-all' ? kickAll : reinitAll
		const successMessage =
			activeModal === 'kick-all'
				? 'เตะผู้เล่นออกจากเกมทั้งหมดเรียบร้อยแล้ว'
				: 'รีเซ็ตเซสชันทั้งหมดเรียบร้อยแล้ว'

		mutationFn(undefined, {
			onSuccess: () => {
				toast.success(successMessage)
				handleCloseModal()
			},
			onError: (error) => {
				showErrorToast(error)
			},
		})
	}

	const getModalConfig = () => {
		if (activeModal === 'kick-all') {
			return {
				title: 'เตะผู้เล่นออกจากเกม',
				description:
					'การดำเนินการนี้จะเตะผู้เล่นทั้งหมดออกจากเกมทันที ใช้ในกรณีที่มีการรั่วไหลของข้อมูล',
				warningText: '⚠️ ผู้เล่นทั้งหมดจะถูกเตะออกจากเกมและต้องกดเข้าเกมใหม่',
				icon: <Power className="h-6 w-6 text-destructive" />,
			}
		}
		return {
			title: 'รีเซ็ตเซสชันทั้งหมด',
			description:
				'การดำเนินการนี้จะรีเซ็ตเซสชันทั้งหมดเพื่อใช้การตั้งค่าใหม่ ใช้เมื่อมีการเปลี่ยนแปลงการตั้งค่าที่สำคัญ',
			warningText: '⚠️ เซสชันทั้งหมดจะถูกรีเซ็ตและใช้การตั้งค่าใหม่ทันที',
			icon: <RefreshCw className="h-6 w-6 text-orange-500" />,
		}
	}

	const modalConfig = activeModal ? getModalConfig() : null

	return (
		<PageContainer scrollable>
			<div className="space-y-4">
				<div>
					<h1 className="text-3xl font-bold tracking-tight">
						จัดการเซสชัน (Session Management)
					</h1>
					<p className="mt-2 text-muted-foreground">
						จัดการเซสชันของผู้เล่นทั้งหมดในระบบ - ใช้เฉพาะกรณีจำเป็นเท่านั้น
					</p>
				</div>

				<Alert variant="destructive">
					<AlertTriangle className="h-4 w-4" />
					<AlertTitle>คำเตือน</AlertTitle>
					<AlertDescription>
						การดำเนินการเหล่านี้มีผลกระทบต่อผู้เล่นทั้งหมดในระบบ
						กรุณาใช้ความระมัดระวังและมั่นใจว่าเป็นการดำเนินการที่จำเป็น
					</AlertDescription>
				</Alert>

				<div className="grid gap-4 md:grid-cols-2">
					{/* Kick All Sessions Card */}
					<Card className="border-destructive/50">
						<CardHeader>
							<div className="flex items-center gap-2">
								<Power className="h-5 w-5 text-destructive" />
								<CardTitle>เตะผู้เล่นออกจากเกม</CardTitle>
							</div>
							<CardDescription>เตะผู้เล่นทั้งหมดออกจากเกมทันที</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="space-y-2 text-sm">
								<p className="font-semibold text-destructive">ผลกระทบ:</p>
								<ul className="list-inside list-disc space-y-1 text-muted-foreground">
									<li>ผู้เล่นทั้งหมดจะถูกเตะออกจากเกมทันที</li>
									<li>ต้องกดเข้าเกมใหม่อีกครั้ง</li>
									<li>เซสชันเกมที่ใช้งานอยู่ทั้งหมดจะสิ้นสุดทันที</li>
								</ul>
							</div>
							<Button
								variant="destructive"
								className="w-full"
								onClick={() => handleOpenModal('kick-all')}
								disabled={isProcessing}
							>
								<Power className="mr-2 h-4 w-4" />
								เตะผู้เล่นออกจากเกม
							</Button>
						</CardContent>
					</Card>

					{/* Reinit All Sessions Card */}
					<Card className="border-orange-500/50">
						<CardHeader>
							<div className="flex items-center gap-2">
								<RefreshCw className="h-5 w-5 text-orange-500" />
								<CardTitle>รีเซ็ตเซสชันทั้งหมด</CardTitle>
							</div>
							<CardDescription>
								รีเซ็ตเซสชันเพื่อใช้การตั้งค่าใหม่
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="space-y-2 text-sm">
								<p className="font-semibold text-orange-600">ผลกระทบ:</p>
								<ul className="list-inside list-disc space-y-1 text-muted-foreground">
									<li>เซสชันทั้งหมดจะถูกรีเซ็ตและใช้การตั้งค่าใหม่</li>
									<li>ผู้เล่นอาจต้องโหลดหน้าใหม่เพื่อรับการตั้งค่าที่อัปเดต</li>
									<li>การเชื่อมต่อที่มีอยู่จะถูกรีเซ็ต</li>
								</ul>
							</div>
							<Button
								variant="outline"
								className="w-full border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
								onClick={() => handleOpenModal('reinit-all')}
								disabled={isProcessing}
							>
								<RefreshCw className="mr-2 h-4 w-4" />
								รีเซ็ตเซสชันทั้งหมด
							</Button>
						</CardContent>
					</Card>
				</div>

				{/* Additional Information */}
				<Card>
					<CardHeader>
						<CardTitle>ข้อมูลเพิ่มเติม</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3 text-sm text-muted-foreground">
						<div>
							<p className="mb-1 font-semibold text-foreground">ความแตกต่าง:</p>
							<ul className="list-inside list-disc space-y-1">
								<li>
									<strong>Kick All:</strong> เตะผู้เล่นออกจากเกมทันที
									ต้องกดเข้าเกมใหม่
								</li>
								<li>
									<strong>Reinit All:</strong>{' '}
									รีเซ็ตเซสชันแต่ผู้เล่นยังอยู่ในเกม
								</li>
							</ul>
						</div>
					</CardContent>
				</Card>

				{/* Player-Specific Actions */}
				<Card className="border-blue-500/50">
					<CardHeader>
						<div className="flex items-center gap-2">
							<User className="h-5 w-5 text-blue-500" />
							<CardTitle>จัดการเซสชันผู้เล่นเฉพาะราย</CardTitle>
						</div>
						<CardDescription>
							เตะหรือรีเซ็ตเซสชันของผู้เล่นคนใดคนหนึ่งโดยใช้เบอร์โทรศัพท์
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 md:grid-cols-2">
							<Button
								variant="outline"
								className="w-full border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
								onClick={() => handleOpenPlayerAction('reinit')}
								disabled={isPlayerProcessing}
							>
								<RefreshCw className="mr-2 h-4 w-4" />
								รีเซ็ตเซสชันผู้เล่น
							</Button>
							<Button
								variant="outline"
								className="w-full border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
								onClick={() => handleOpenPlayerAction('kick')}
								disabled={isPlayerProcessing}
							>
								<Power className="mr-2 h-4 w-4" />
								เตะผู้เล่นออกจากเกม
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Confirmation Modal */}
			{activeModal && modalConfig && (
				<Dialog open onOpenChange={handleCloseModal}>
					<DialogContent className="sm:max-w-[500px]">
						<DialogHeader>
							<div className="flex items-center gap-3">
								{modalConfig.icon}
								<div>
									<DialogTitle>{modalConfig.title}</DialogTitle>
									<DialogDescription className="mt-1">
										{modalConfig.description}
									</DialogDescription>
								</div>
							</div>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<Alert
								variant={activeModal === 'kick-all' ? 'destructive' : 'default'}
							>
								<AlertTriangle className="h-4 w-4" />
								<AlertTitle>คำเตือนสำคัญ</AlertTitle>
								<AlertDescription>{modalConfig.warningText}</AlertDescription>
							</Alert>

							<div className="space-y-2">
								<p className="text-sm font-medium">
									กรุณาพิมพ์{' '}
									<span className="font-bold">&quot;ยืนยัน&quot;</span>{' '}
									เพื่อดำเนินการ
								</p>
								<Input
									value={confirmText}
									onChange={(e) => setConfirmText(e.target.value)}
									placeholder="ยืนยัน"
									disabled={isProcessing}
									autoFocus
									onKeyDown={(e) => {
										if (e.key === 'Enter' && isConfirmTextValid) {
											handleConfirm()
										}
									}}
								/>
								<p className="text-xs text-muted-foreground">
									การดำเนินการนี้ไม่สามารถย้อนกลับได้
								</p>
							</div>
						</div>

						<DialogFooter>
							<Button
								variant="outline"
								onClick={handleCloseModal}
								disabled={isProcessing}
							>
								ยกเลิก
							</Button>
							<Button
								variant={activeModal === 'kick-all' ? 'destructive' : 'default'}
								onClick={handleConfirm}
								disabled={!isConfirmTextValid || isProcessing}
							>
								{isProcessing ? 'กำลังดำเนินการ...' : 'ยืนยัน'}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}

			{/* Kick Player Dialog */}
			<AlertDialog
				open={playerActionDialog === 'kick'}
				onOpenChange={(open) => !open && setPlayerActionDialog(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>เตะผู้เล่นออกจากเกม</AlertDialogTitle>
						<AlertDialogDescription>
							ผู้เล่นจะถูกเตะออกจากเกมทันทีและต้องกดเข้าเกมใหม่อีกครั้ง
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="space-y-2 py-4">
						<Label htmlFor="kick-player-phone">เบอร์โทรศัพท์ผู้เล่น</Label>
						<Input
							id="kick-player-phone"
							value={playerPhone}
							onChange={(e) => setPlayerPhone(e.target.value)}
							placeholder="กรอกเบอร์โทรศัพท์ (เช่น 0812345678)"
							disabled={isPlayerProcessing}
							autoFocus
							onKeyDown={(e) => {
								if (e.key === 'Enter' && playerPhone.trim()) {
									handlePlayerKick()
								}
							}}
						/>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isPlayerProcessing}>
							ยกเลิก
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handlePlayerKick}
							disabled={!playerPhone.trim() || isPlayerProcessing}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{isPlayerProcessing
								? 'กำลังค้นหาและดำเนินการ...'
								: 'เตะออกจากเกม'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Reinit Player Dialog */}
			<AlertDialog
				open={playerActionDialog === 'reinit'}
				onOpenChange={(open) => !open && setPlayerActionDialog(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>รีเซ็ตเซสชันผู้เล่น</AlertDialogTitle>
						<AlertDialogDescription>
							เซสชันของผู้เล่นจะถูกรีเซ็ตและใช้การตั้งค่าใหม่ทันที
							ผู้เล่นอาจต้องโหลดหน้าใหม่เพื่อรับการตั้งค่าที่อัปเดต
						</AlertDialogDescription>
					</AlertDialogHeader>
					<div className="space-y-2 py-4">
						<Label htmlFor="reinit-player-phone">เบอร์โทรศัพท์ผู้เล่น</Label>
						<Input
							id="reinit-player-phone"
							value={playerPhone}
							onChange={(e) => setPlayerPhone(e.target.value)}
							placeholder="กรอกเบอร์โทรศัพท์ (เช่น 0812345678)"
							disabled={isPlayerProcessing}
							autoFocus
							onKeyDown={(e) => {
								if (e.key === 'Enter' && playerPhone.trim()) {
									handlePlayerReinit()
								}
							}}
						/>
					</div>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isPlayerProcessing}>
							ยกเลิก
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handlePlayerReinit}
							disabled={!playerPhone.trim() || isPlayerProcessing}
						>
							{isPlayerProcessing
								? 'กำลังค้นหาและดำเนินการ...'
								: 'รีเซ็ตเซสชัน'}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContainer>
	)
}
